import OutlinedExportButton from '../components/OutlinedExportButton';
import { AnalyticsHeaderSlotContext } from './AnalyticsHeaderSlot';
import {
    Autocomplete,
    Box,
    Chip,
    Grid,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import moment from 'moment-timezone';
import React from 'react';
import ReactDOM from 'react-dom';

import TimezonePicker from '../components/TimezonePicker';
import { useIsWidthUp } from '../utils';

const useStyles = makeStyles((theme) => ({
    toolbarRoot: {
        margin: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
        maxWidth: '90vw !important',
    },
    pillButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '32px',
        padding: '0px 16px',
        borderRadius: '20px',
        gap: '4px',
        color: 'black',
        backgroundColor: '#D8D8D8',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: {
            height: '28px',
            padding: '0px 10px',
            borderRadius: '18px',
        },
    },
    selectedPillButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '32px',
        padding: '0px 16px',
        borderRadius: '20px',
        gap: '4px',
        backgroundColor: '#007AFF',
        color: 'white',
        cursor: 'pointer',
        [theme.breakpoints.down('sm')]: {
            height: '28px',
            padding: '0px 10px',
            borderRadius: '18px',
        },
    },
}));

const MEDIUM_OPTIONS = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'sms', label: 'SMS' },
    { value: 'email', label: 'Email' },
    { value: 'chat', label: 'In-app chat' },
    { value: 'airbnb', label: 'Airbnb' },
];

function NotificationMetricsRangePickerActionBar({
    metricsTimezone,
    onMetricsTimezoneChange,
    liveRangeMaxDaysGap,
    liveRangeClampFlash,
    ...pickerActionBarProps
}) {
    const z = metricsTimezone || 'UTC';
    const { className, sx: layoutSx, ...pickersActionBarOnlyProps } = pickerActionBarProps;
    const showLiveRangeHint = liveRangeMaxDaysGap != null;
    return (
        <Box
            className={className}
            sx={[
                {
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    borderTop: 1,
                    borderColor: 'divider',
                },
                ...(Array.isArray(layoutSx) ? layoutSx : [layoutSx].filter(Boolean)),
            ]}
        >
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                <Tooltip
                    arrow
                    title={`Calendar day range uses: ${z}. Notification timestamps are stored in UTC.`}
                    placement="top-start"
                >
                    <Box sx={{ width: '100%', minWidth: { xs: 0, sm: 260 }, maxWidth: '100%' }}>
                        <TimezonePicker
                            label="Timezone"
                            variant="outlined"
                            size="small"
                            fullWidth
                            includeUtc
                            showTimezoneOffset
                            value={z}
                            onChange={(_ev, option) => onMetricsTimezoneChange(option?.value || 'UTC')}
                            componentsProps={{
                                paper: { sx: { maxWidth: 'min(560px, 94vw)' } },
                            }}
                        />
                    </Box>
                </Tooltip>
            </Box>
            {showLiveRangeHint && (
                <Box sx={{ px: 2, pb: 0 }}>
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{
                            display: 'block',
                            color: liveRangeClampFlash ? 'warning.dark' : 'text.secondary',
                            fontWeight: liveRangeClampFlash ? 600 : 400,
                            lineHeight: 1.35,
                        }}
                    >
                        {liveRangeClampFlash
                            ? `End date was adjusted to fit the ${liveRangeMaxDaysGap}-day maximum for this live view.`
                            : `Live view: longest range is ${liveRangeMaxDaysGap} calendar days. Extra days are disabled while you choose start and end.`}
                    </Typography>
                </Box>
            )}
            <PickersActionBar {...pickersActionBarOnlyProps} />
        </Box>
    );
}

export const NotificationMetricsListToolbar = (props) => {
    const classes = useStyles();
    const {
        recordListFilters,
        setRecordListFilters,
        metricsTimezone,
        onMetricsTimezoneChange,
        earliestAvailableDate,
        restrictLiveDateRange = false,
        liveMaxRangeDaysGap = 90,
    } = props;
    const isSmallScreen = !useIsWidthUp('sm');
    const [liveRangeClampFlash, setLiveRangeClampFlash] = React.useState(false);

    React.useEffect(() => {
        if (!liveRangeClampFlash) return undefined;
        const t = window.setTimeout(() => setLiveRangeClampFlash(false), 5000);
        return () => window.clearTimeout(t);
    }, [liveRangeClampFlash]);

    const z = metricsTimezone || 'UTC';

    const earliestMoment = React.useMemo(() => {
        if (!earliestAvailableDate) return null;
        const m = moment.tz(earliestAvailableDate, 'YYYY-MM-DD', z).startOf('day');
        return m.isValid() ? m : null;
    }, [earliestAvailableDate, z]);

    const pillOptions = React.useMemo(() => {
        // The "all available" pill clears the date filter, which the backend
        // resolves to [earliest_checkpoint .. today]. Label it with the
        // actual earliest date so the user sees the real range instead of an
        // ambiguous "All Time" — and it self-updates as backfills extend
        // the floor backward.
        const allAvailableLabel = earliestMoment
            ? `Since ${earliestMoment.format('MMM D, YYYY')}`
            : 'All Available';
        const opts = [
            { id: 'all time', label: allAvailableLabel, days: null },
            { id: 'last 7', label: 'Last 7', days: 7 },
            { id: 'last 30', label: 'Last 30', days: 30 },
            { id: 'last 90', label: 'Last 90', days: 90 },
            { id: 'last 365', label: 'Last 365', days: 365 },
        ].filter((opt) => {
            if (!restrictLiveDateRange) return true;
            // Live UX: forbid unbounded ranges and presets beyond server-friendly windows.
            if (opt.id === 'all time' || opt.id === 'last 365') return false;
            return true;
        });
        if (!earliestMoment) return opts;
        // Hide pills whose lookback would extend before the earliest available
        // bucket — better UX than showing them grayed out, since the user has
        // no control over backfill depth from this surface.
        const todayLocal = moment.tz(z).startOf('day');
        const availableDays = todayLocal.diff(earliestMoment, 'days');
        return opts.filter((opt) => opt.days == null || opt.days <= availableDays);
    }, [earliestMoment, z, restrictLiveDateRange]);

    const NotificationMetricsRangePickerActionBarSlot = React.useMemo(
        () => function NotificationMetricsRangePickerActionBarSlot(slotProps) {
            return (
                <NotificationMetricsRangePickerActionBar
                    {...slotProps}
                    metricsTimezone={metricsTimezone}
                    onMetricsTimezoneChange={onMetricsTimezoneChange}
                    liveRangeMaxDaysGap={restrictLiveDateRange ? liveMaxRangeDaysGap : undefined}
                    liveRangeClampFlash={restrictLiveDateRange ? liveRangeClampFlash : false}
                />
            );
        },
        [metricsTimezone, onMetricsTimezoneChange, restrictLiveDateRange, liveMaxRangeDaysGap, liveRangeClampFlash],
    );

    const handleDateFilterSelect = (option) => {
        const todays_date = moment.tz(z).format('YYYY-MM-DD');
        let anchor = moment.tz(z);
        let date_filter;

        if (option === 'all time') {
            setRecordListFilters((prev) => {
                const f_start = prev.find((f) => f.field === 'start_datetime');
                const f_end = prev.find((f) => f.field === 'end_datetime');
                return [
                    ...prev.filter((f) => f.field !== 'start_datetime' && f.field !== 'end_datetime'),
                    { ...f_start, value: null },
                    { ...f_end, value: null },
                ];
            });
            return;
        }

        if (option === 'last 7') anchor = anchor.subtract(7, 'days');
        else if (option === 'last 30') anchor = anchor.subtract(30, 'days');
        else if (option === 'last 90') anchor = anchor.subtract(90, 'days');
        else if (option === 'last 365') anchor = anchor.subtract(365, 'days');

        date_filter = anchor.format('YYYY-MM-DD');
        if (earliestMoment) {
            const a = moment.tz(date_filter, 'YYYY-MM-DD', z).startOf('day');
            if (a.isBefore(earliestMoment)) {
                date_filter = earliestMoment.format('YYYY-MM-DD');
            }
        }

        setRecordListFilters((prev) => {
            const f_start = prev.find((f) => f.field === 'start_datetime');
            const f_end = prev.find((f) => f.field === 'end_datetime');
            return [
                ...prev.filter((f) => f.field !== 'start_datetime' && f.field !== 'end_datetime'),
                { ...f_start, value: date_filter },
                { ...f_end, value: todays_date },
            ];
        });
    };

    const isThisDateFilterSelected = (option) => {
        const todays_date = moment.tz(z).format('YYYY-MM-DD');
        const start = recordListFilters.find((f) => f.field === 'start_datetime')?.value;
        const end = recordListFilters.find((f) => f.field === 'end_datetime')?.value;

        if (option === 'all time') {
            return start == null && (end == null || end === todays_date);
        }
        let want = null;
        if (option === 'last 7') want = moment.tz(z).subtract(7, 'days').format('YYYY-MM-DD');
        else if (option === 'last 30') want = moment.tz(z).subtract(30, 'days').format('YYYY-MM-DD');
        else if (option === 'last 90') want = moment.tz(z).subtract(90, 'days').format('YYYY-MM-DD');
        else if (option === 'last 365') want = moment.tz(z).subtract(365, 'days').format('YYYY-MM-DD');
        return want != null && start === want && end === todays_date;
    };

    const handleDateRangeChange = (dateRange) => {
        let dateStart = dateRange[0];
        let dateEnd = dateRange[1];
        if (dateStart) dateStart = moment(dateStart).tz(z).format('YYYY-MM-DD');
        else dateStart = null;
        if (dateEnd) dateEnd = moment(dateEnd).tz(z).format('YYYY-MM-DD');
        else dateEnd = null;

        // Live UX: disallow an unbounded (null-null) window from "Clear".
        if (restrictLiveDateRange && !dateStart && !dateEnd) {
            dateStart = moment.tz(z).subtract(7, 'days').format('YYYY-MM-DD');
            dateEnd = moment.tz(z).format('YYYY-MM-DD');
        }

        const bumpIsoToRollupFloor = (iso) => {
            if (iso == null || iso === '' || !earliestMoment) return iso;
            const m = moment.tz(iso, 'YYYY-MM-DD', z).startOf('day');
            if (!m.isValid()) return iso;
            return m.isBefore(earliestMoment) ? earliestMoment.format('YYYY-MM-DD') : iso;
        };
        dateStart = bumpIsoToRollupFloor(dateStart);
        dateEnd = bumpIsoToRollupFloor(dateEnd);
        if (dateStart != null && dateEnd != null) {
            const ms = moment.tz(dateStart, 'YYYY-MM-DD', z).startOf('day');
            const me = moment.tz(dateEnd, 'YYYY-MM-DD', z).startOf('day');
            if (me.isBefore(ms)) {
                dateEnd = dateStart;
            }
        }
        if (
            restrictLiveDateRange &&
            liveMaxRangeDaysGap != null &&
            dateStart &&
            dateEnd
        ) {
            const ms = moment.tz(dateStart, 'YYYY-MM-DD', z).startOf('day');
            const me = moment.tz(dateEnd, 'YYYY-MM-DD', z).startOf('day');
            if (me.diff(ms, 'days') > liveMaxRangeDaysGap) {
                dateEnd = ms.clone().add(liveMaxRangeDaysGap, 'days').format('YYYY-MM-DD');
                setLiveRangeClampFlash(true);
            }
        }

        setRecordListFilters((prev) => {
            const f_start = prev.find((f) => f.field === 'start_datetime');
            const f_end = prev.find((f) => f.field === 'end_datetime');
            const prevStart = f_start?.value ?? null;
            const startChanged = (prevStart || null) !== (dateStart || null);
            // MUI keeps the previous end in state until the second date is chosen.
            // Clearing end when start changes avoids firing a giant [newStart..oldEnd] query.
            const nextEnd = startChanged ? null : dateEnd;
            return [
                ...prev.filter((f) => f.field !== 'start_datetime' && f.field !== 'end_datetime'),
                { ...f_start, value: dateStart },
                { ...f_end, value: nextEnd },
            ];
        });
    };

    const rangePickerValue = React.useMemo(() => {
        const s = recordListFilters.find((f) => f.field === 'start_datetime')?.value;
        const e = recordListFilters.find((f) => f.field === 'end_datetime')?.value;
        return [
            s ? moment.tz(s, 'YYYY-MM-DD', z).startOf('day') : null,
            e ? moment.tz(e, 'YYYY-MM-DD', z).startOf('day') : null,
        ];
    }, [recordListFilters, z]);

    const shouldDisableCalendarDay = React.useCallback(
        (day, selectionPosition) => {
            const d = moment(day).tz(z).startOf('day');
            if (!d.isValid()) return false;
            const today = moment.tz(z).startOf('day');
            if (restrictLiveDateRange && d.isAfter(today)) return true;
            if (earliestMoment && d.isBefore(earliestMoment)) return true;

            if (!restrictLiveDateRange || liveMaxRangeDaysGap == null) {
                return false;
            }

            const [pickerStartMoment, pickerEndMoment] = rangePickerValue;

            // 90-day graying is an affordance while choosing the missing edge only.
            // Rollup/future rules above still apply with a full range selected.
            const bothDatesPicked = pickerStartMoment != null && pickerEndMoment != null;
            if (bothDatesPicked) {
                return false;
            }

            if (selectionPosition === 'end') {
                if (!pickerStartMoment) return false;
                const anchor = moment(pickerStartMoment).tz(z).startOf('day');
                const gap = d.diff(anchor, 'days');
                return gap < 0 || gap > liveMaxRangeDaysGap;
            }

            if (pickerEndMoment) {
                const endDay = moment(pickerEndMoment).tz(z).startOf('day');
                const gap = endDay.diff(d, 'days');
                return gap < 0 || gap > liveMaxRangeDaysGap;
            }

            return false;
        },
        [restrictLiveDateRange, liveMaxRangeDaysGap, z, earliestMoment, rangePickerValue],
    );

    const currentMediumValues = React.useMemo(() => {
        const v = recordListFilters.find((f) => f.field === 'mediums')?.value;
        if (!v) return [];
        return MEDIUM_OPTIONS.filter((o) => v.includes(o.value));
    }, [recordListFilters]);

    const handleMediumChange = (_ev, selected) => {
        const ids = (selected || []).map((s) => s.value);
        setRecordListFilters((prev) => {
            const f = prev.find((f) => f.field === 'mediums');
            return [
                ...prev.filter((f) => f.field !== 'mediums'),
                { ...f, value: ids.length ? ids : null, op: 'isAnyOf' },
            ];
        });
    };

    const handleExportCSV = async () => {
        await props.onExportList?.();
    };
    const headerSlotEl = React.useContext(AnalyticsHeaderSlotContext);

    // Render the Export Report button via a portal directly into <body>.
    // We previously had `position: fixed` on the button (or a wrapping
    // Box) inside the toolbar, but *something* up the React tree
    // (likely an MUI Drawer/AppBar transform, or a third-party
    // library composing transform/will-change for compositing) creates
    // a containing block, which makes `position: fixed` resolve to
    // that ancestor instead of the viewport — so the button scrolls
    // with the page. Portaling to <body> escapes every React-tree
    // ancestor and guarantees viewport-relative pinning. On a phone inside
    // the Analytics page it sits in the title row instead (CP-8289).
    const inHeaderSlot = isSmallScreen && headerSlotEl;
    const exportButtonPortal = ReactDOM.createPortal(
        <OutlinedExportButton
            onClick={handleExportCSV}
            label="Export Report"
            tooltip="Download a plain-text Notification Metrics report for the current date range and timezone"
            anchorStyle={inHeaderSlot ? undefined : isSmallScreen
                ? { position: 'absolute', right: 0, top: 65, zIndex: 1100 }
                : { position: 'absolute', right: 10, top: 10, zIndex: 1100 }}
        />,
        inHeaderSlot ? headerSlotEl : document.body,
    );

    return (
        <Toolbar disableGutters={true} className={classes.toolbarRoot}>
            {exportButtonPortal}

            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={12} container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DateRangePicker
                                slots={{
                                    field: SingleInputDateRangeField,
                                    actionBar: NotificationMetricsRangePickerActionBarSlot,
                                }}
                                name="allowedRange"
                                variant="filled"
                                onChange={handleDateRangeChange}
                                format="MMMM D, YYYY"
                                timezone={z}
                                autoOk
                                label="Dates"
                                disableFuture={restrictLiveDateRange}
                                maxDate={restrictLiveDateRange ? moment.tz(z).startOf('day') : undefined}
                                minDate={earliestMoment || undefined}
                                shouldDisableDate={
                                    restrictLiveDateRange || earliestMoment ? shouldDisableCalendarDay : undefined
                                }
                                value={rangePickerValue}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        variant: 'filled',
                                        fullWidth: true,
                                        readOnly: true,
                                        sx: { '& .MuiFilledInput-input': { cursor: 'pointer' } },
                                    },
                                    actionBar: { actions: ['clear', 'accept'] },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={MEDIUM_OPTIONS}
                            getOptionLabel={(o) => o.label}
                            isOptionEqualToValue={(o, v) => o.value === v.value}
                            value={currentMediumValues}
                            onChange={handleMediumChange}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip variant="outlined" size="small" label={option.label} {...getTagProps({ index })} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} variant="filled" size="small" label="Mediums" />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: isSmallScreen ? 0.5 : 1, flexWrap: 'wrap' }}>
                            {pillOptions.map((opt) => {
                                const selected = isThisDateFilterSelected(opt.id);
                                return (
                                    <Box
                                        key={opt.id}
                                        className={selected ? classes.selectedPillButton : classes.pillButton}
                                        onClick={() => handleDateFilterSelect(opt.id)}
                                        style={{ minWidth: isSmallScreen ? 70 : 95 }}
                                    >
                                        <Typography fontSize={isSmallScreen ? 12 : 14} style={{ whiteSpace: 'nowrap' }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Grid>
                </Grid>
            </Grid>
        </Toolbar>
    );
};

export default NotificationMetricsListToolbar;
